import Vue from 'vue'
import VueRouter from 'vue-router'
import store from '@/store'
import AuthActions from '@/store/core/auth/actions-types'
import AuthMutations from '@/store/core/auth/mutations-types'
import AppActions from '@/store/app/actions-types'
import AppMutations from '@/store/app/mutations-types'
import AclAPI from '@/services/api/business/acl'
import UserRoles from '@/store/core/user/user-roles'


import coreRoutes from './core'
import operationRoutes from './operation'
import cdpRoutes from './cdp'
import contentRoutes from './content'
import aiWorkforceRoutes from './ai_workforce'
import jobRoutes from './job'
import businessRoutes from './business'

Vue.use(VueRouter)

const BUSINESS_EDIT_ACCESS_LEVEL = 4
const BUSINESS_SETTINGS_ROUTES = [
  'DeliverableSettings',
  'BusinessCustomers',
  'BusinessRatecard',
  'CustomerDeliverables',
  'CustomerBillables',
  'CustomerStatuses',
  'CustomerAcls',
]

const baseRoutes = [
  {
    path: '/',
    name: 'Home',
    component: () => import('../views/Home.vue'),
    meta: {
      public: false
    }
  },
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/core/auth/Login.vue'),
    meta: {
      public: true,
      onlyLoggedOut: true
    }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: () => import('../views/core/auth/ResetPassword.vue'),
    meta: {
      public: false,
      onlyLoggedOut: false
    }
  },
  {
    path: '/settings',
    name: 'Settings',
    component: () => import('../views/Settings.vue'),
    meta: {
      public: false,
      onlyLoggedOut: false
    }
  },
]

const routes = baseRoutes
  .concat(coreRoutes)
  .concat(operationRoutes)
  .concat(cdpRoutes)
  .concat(contentRoutes)
  .concat(aiWorkforceRoutes)
  .concat(jobRoutes)
  .concat(businessRoutes)

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  // A route change does not unload the application: uploads are held in Vuex
  // and continue in the background. Abort cleanup is only needed when the
  // browser page is actually closed or reloaded.

  // A scoped public link is intentionally independent of the signed-in user.
  // Its view validates the scoped token before making any feature requests.
  if (to.matched.some(record => record.meta.publicScopedToken)) {
    return next()
  }

  store
    .dispatch('core/auth/' + AuthActions.CHECK_TOKEN)
    .then(async () => {
      const authenticated = store.state.core.auth.authenticated
      const userRoles = store.state.core.auth.userRoles
      const resetPwd = store.state.core.auth.userData.reset_pwd || false
      const onlyLoggedOut = to.matched.some(record => record.meta.onlyLoggedOut)
      const isPublic = to.matched.some(record => record.meta.public)
      const accessRoles = to.meta.accessRoles || []

      if (!isPublic && !authenticated) {
        return next({
          path: '/login',
          query: { redirect: to.fullPath }
        })
      }

      // Ensure the user's application instances are loaded before validating
      // app-scoped routes. On a fresh tab the Vuex state starts empty while a
      // token exists in localStorage; if userApplications has not been
      // populated yet (e.g. a token renewal superseded the initial load) we
      // fetch it here so the checks below don't wrongly redirect to home.
      if (authenticated && (!Array.isArray(store.state.app.userApplications) || store.state.app.userApplications.length === 0)) {
        try {
          await store.dispatch(`app/${AppActions.GET_AND_SET_USER_APPLICATIONS}`)
        } catch (error) {
          console.log('Error loading user applications', error)
        }
      }

      const userApplications = store.state.app.userApplications


      if (resetPwd && to.fullPath !== "/reset-password") {
        return next({
          path: '/reset-password'
        })
      }

      if (authenticated && onlyLoggedOut) {
        return next('/')
      }

      if (accessRoles.length > 0) {
        const hasAccessRights = userRoles.some(r => accessRoles.includes(r))

        if (! hasAccessRights) {
          return next({
            path: '/',
            query: { redirect: to.fullPath }
          })
        }
      }

      if (to.matched.some(record => record.path.startsWith('/content'))) {
        if (from.params.contentAppId) {
          to.params.appId = from.params.contentAppId
        }

        const appId = to.params.appId

        if (appId) {
          const foundAppInstance = userApplications.some(app => {
            return app.application_instance_id == appId && app.application_key === 'content'
          })
          if (! foundAppInstance) {
            return next('/')
          }

          store.commit(`app/${AppMutations.SET_SELECTED_CONTENT_APP}`, appId)
        }
      }

      if (to.matched.some(record => record.path.startsWith('/business'))) {
        if (from.params.businessAppId) {
          to.params.appId = from.params.businessAppId
        }

        const appId = to.params.appId

        if (appId) {
          const foundAppInstance = userApplications.some(app => app.application_instance_id == appId)
          if (! foundAppInstance) {
            return next('/')
          }

          store.commit(`app/${AppMutations.SET_SELECTED_BUSINESS_APP}`, appId)
        }

        const hasAdminBusinessAccess = userRoles.includes(UserRoles.USER_ADMIN) || userRoles.includes(UserRoles.USER_MASTER)
        const requiresBusinessEditAccess = BUSINESS_SETTINGS_ROUTES.includes(to.name)

        if (!hasAdminBusinessAccess && requiresBusinessEditAccess) {
          try {
            const params = to.params.customerId ? { customer_id: to.params.customerId } : {}
            const response = await AclAPI.getMyAcls(params)
            const aclItems = response.data.items || response.data || []
            const acls = Array.isArray(aclItems) ? aclItems : [aclItems]
            const scopedAcls = to.params.customerId
              ? acls.filter(acl => String(acl.customer_id) === String(to.params.customerId))
              : acls
            const accessLevel = scopedAcls.reduce((combined, acl) => {
              return combined | (acl.access_level || 0)
            }, 0)
            const hasBusinessEditAcl = (accessLevel & BUSINESS_EDIT_ACCESS_LEVEL) !== 0

            if (!hasBusinessEditAcl && to.name !== 'DeliverableSettings') {
              return next({
                name: 'DeliverableSettings',
                params: { appId: to.params.appId },
              })
            }
          } catch (error) {
            if (to.name !== 'DeliverableSettings') {
              return next({
                name: 'DeliverableSettings',
                params: { appId: to.params.appId },
              })
            }
          }
        }
      }

      if (!isPublic && to.matched.some(record => record.path.startsWith('/job'))) {
        const appId = to.params.appId

        const foundAppInstance = userApplications.some(app => {
          return app.application_instance_id == appId && app.application_key === 'job'
        })
        if (! foundAppInstance) {
          return next('/')
        }

        store.commit(`app/${AppMutations.SET_SELECTED_JOB_APP}`, appId)

        const contentAppId = to.params.contentAppId
        const foundContentAppInstance = userApplications.some(app => {
          return app.application_instance_id == contentAppId && app.application_key === 'content'
        })
        if (foundContentAppInstance) {
          store.commit(`app/${AppMutations.SET_SELECTED_CONTENT_APP}`, contentAppId)
        }

        const businessAppId = to.params.businessAppId
        const foundBusinessAppInstance = userApplications.some(app => app.application_instance_id == businessAppId)
        if (foundBusinessAppInstance) {
          store.commit(`app/${AppMutations.SET_SELECTED_BUSINESS_APP}`, businessAppId)
        }
      }

      if (to.matched.some(record => record.path.startsWith('/cdp'))) {
        const appId = to.params.appId

        const foundAppInstance = userApplications.some(app => app.application_instance_id == appId)
        if (! foundAppInstance) {
          return next('/')
        }

        store.commit(`app/${AppMutations.SET_SELECTED_CDP_APP}`, appId)
      }

      if (to.matched.some(record => record.path.startsWith('/ai_workforce'))) {
        const appId = to.params.appId

        const foundAppInstance = userApplications.some(app => app.application_instance_id == appId)
        if (foundAppInstance) {
          store.commit(`app/${AppMutations.SET_SELECTED_AI_WORKFORCE_APP}`, appId)
        }

        const contentAppId = to.params.contentAppId
        const foundContentAppInstance = userApplications.some(app => {
          return app.application_instance_id == contentAppId && app.application_key === 'content'
        })
        if (foundContentAppInstance) {
          store.commit(`app/${AppMutations.SET_SELECTED_CONTENT_APP}`, contentAppId)
        }
      }

      if(to.name !== 'Login' && to.name !== 'ResetPassword') {
        store.commit(`core/auth/${AuthMutations.SET_LAST_PATH}`, to.fullPath)
      }

      next()
    })
    .catch(() => {
      console.log('Error checking token')
      return next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
    })
})

export default router
